import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiRefreshCw, FiUsers, FiDollarSign, FiAlertTriangle, FiCheckCircle, FiClock, FiXCircle } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

interface ReconciliationData {
  snapshot_at: string;
  users: {
    total: number;
    trialing: number;
    active_paying: number;
    expired: number;
    no_plan: number;
  };
  users_by_plan: {
    starter: number;
    pro_trader: number;
    institutional_ai: number;
    enterprise: number;
  };
  invoices: {
    total: number;
    pending: number;
    paid: number;
    expired: number;
    total_revenue_usd: number;
  };
  anomalies: {
    active_but_expired_paid_until: number;
    paid_until_without_plan: number;
  };
}

const BillingReconciliation: React.FC = () => {
  const [data, setData] = useState<ReconciliationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReconciliation = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: result, error: rpcError } = await supabase.rpc('admin_get_billing_reconciliation');
      
      if (rpcError) throw rpcError;
      
      if (result?.success) {
        setData(result);
      } else {
        throw new Error(result?.error || 'Failed to fetch reconciliation data');
      }
    } catch (err: any) {
      console.error('Reconciliation error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReconciliation();
  }, []);

  const hasAnomalies = data && (data.anomalies.active_but_expired_paid_until > 0 || data.anomalies.paid_until_without_plan > 0);

  return (
    <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-purple-600/20 to-cyan-600/20">
            <FiDollarSign className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Billing Reconciliation</h3>
            <p className="text-sm text-muted-foreground">
              {data ? `Snapshot: ${new Date(data.snapshot_at).toLocaleString()}` : 'Loading...'}
            </p>
          </div>
        </div>
        <button
          onClick={fetchReconciliation}
          disabled={loading}
          className="p-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 text-white hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <p className="text-red-400">{error}</p>
        </div>
      )}

      {data && (
        <div className="space-y-6">
          {/* Anomaly Alert */}
          {hasAnomalies && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
            >
              <div className="flex items-center gap-2 mb-2">
                <FiAlertTriangle className="w-5 h-5 text-red-400" />
                <span className="font-semibold text-red-400">Billing Anomalies Detected</span>
              </div>
              <ul className="text-sm text-red-300 space-y-1">
                {data.anomalies.active_but_expired_paid_until > 0 && (
                  <li>• {data.anomalies.active_but_expired_paid_until} users marked active but paid_until has passed</li>
                )}
                {data.anomalies.paid_until_without_plan > 0 && (
                  <li>• {data.anomalies.paid_until_without_plan} users with paid_until but no plan assigned</li>
                )}
              </ul>
              <p className="text-xs text-red-400/70 mt-2">
                Run billing-renewal-check to fix these anomalies.
              </p>
            </motion.div>
          )}

          {/* User Stats */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <FiUsers className="w-4 h-4" /> User Status
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard label="Total" value={data.users.total} color="purple" />
              <StatCard label="Trialing" value={data.users.trialing} color="cyan" icon={<FiClock className="w-3 h-3" />} />
              <StatCard label="Active Paying" value={data.users.active_paying} color="green" icon={<FiCheckCircle className="w-3 h-3" />} />
              <StatCard label="Expired" value={data.users.expired} color="red" icon={<FiXCircle className="w-3 h-3" />} />
              <StatCard label="No Plan" value={data.users.no_plan} color="gray" />
            </div>
          </div>

          {/* Users by Plan */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3">Active Users by Plan</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <PlanCard plan="Starter" count={data.users_by_plan.starter} price={29} />
              <PlanCard plan="Pro Trader" count={data.users_by_plan.pro_trader} price={79} />
              <PlanCard plan="Institutional AI" count={data.users_by_plan.institutional_ai} price={149} />
              <PlanCard plan="Enterprise" count={data.users_by_plan.enterprise} price={null} />
            </div>
          </div>

          {/* Invoice Stats */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <FiDollarSign className="w-4 h-4" /> Crypto Invoices
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <StatCard label="Total" value={data.invoices.total} color="purple" />
              <StatCard label="Pending" value={data.invoices.pending} color="yellow" />
              <StatCard label="Paid" value={data.invoices.paid} color="green" />
              <StatCard label="Expired" value={data.invoices.expired} color="red" />
              <StatCard 
                label="Revenue" 
                value={`$${data.invoices.total_revenue_usd.toLocaleString()}`} 
                color="cyan" 
                isLarge 
              />
            </div>
          </div>

          {/* MRR Estimate */}
          <div className="p-4 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 rounded-xl border border-purple-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Estimated MRR</p>
                <p className="text-2xl font-bold text-foreground">
                  ${(
                    data.users_by_plan.starter * 29 +
                    data.users_by_plan.pro_trader * 79 +
                    data.users_by_plan.institutional_ai * 149
                  ).toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-green-400">
                  ${data.invoices.total_revenue_usd.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ 
  label, 
  value, 
  color, 
  icon,
  isLarge 
}: { 
  label: string; 
  value: number | string; 
  color: string;
  icon?: React.ReactNode;
  isLarge?: boolean;
}) => {
  const colorClasses: Record<string, string> = {
    purple: 'border-purple-500/30 bg-purple-500/10',
    cyan: 'border-cyan-500/30 bg-cyan-500/10',
    green: 'border-green-500/30 bg-green-500/10',
    red: 'border-red-500/30 bg-red-500/10',
    yellow: 'border-yellow-500/30 bg-yellow-500/10',
    gray: 'border-gray-500/30 bg-gray-500/10',
  };

  return (
    <div className={`p-3 rounded-xl border ${colorClasses[color]}`}>
      <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
        {icon}
        {label}
      </div>
      <p className={`font-bold text-foreground ${isLarge ? 'text-lg' : 'text-xl'}`}>{value}</p>
    </div>
  );
};

const PlanCard = ({ plan, count, price }: { plan: string; count: number; price: number | null }) => (
  <div className="p-3 rounded-xl border border-border/50 bg-background/50">
    <p className="text-xs text-muted-foreground">{plan}</p>
    <p className="text-xl font-bold text-foreground">{count}</p>
    {price && <p className="text-xs text-muted-foreground">${price}/mo</p>}
  </div>
);

export default BillingReconciliation;
